import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { getUserTypePreference } from '../util/dataExtractor';
import { SharetribeApi } from '../util/api';
import { StatusCode } from '../util/enums';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CHECK_USER_STRIPE_CONNECTION_REQUEST = 'app/user/CHECK_USER_STRIPE_CONNECTION_REQUEST';
export const CHECK_USER_STRIPE_CONNECTION_SUCCESS = 'app/user/CHECK_USER_STRIPE_CONNECTION_SUCCESS';
export const CHECK_USER_STRIPE_CONNECTION_ERROR = 'app/user/CHECK_USER_STRIPE_CONNECTION_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const UPDATE_USER_WALLET_REQUEST = 'app/user/UPDATE_USER_WALLET_REQUEST';
export const UPDATE_WALLET_SUCCESS = 'app/user/UPDATE_WALLET_SUCCESS';
export const UPDATE_USER_WALLET_ERROR = 'app/user/UPDATE_USER_WALLET_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowTimestamp: 0,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  checkUserStripeConnectionInProgress: false,
  checkUserStripeConnectionError: null,
  stripeConnected: false,
  updateUserWalletInProgress: false,
  userUserWalletError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserShowTimestamp: payload ? new Date().getTime() : 0,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS: {
      const { transactions = [], unreadCount } = payload || {};
      const count = typeof unreadCount === 'number' ? unreadCount : transactions.length;

      return {
        ...state,
        currentUserNotificationCount: count,
      };
    }

    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case CHECK_USER_STRIPE_CONNECTION_REQUEST:
      return {
        ...state,
        checkUserStripeConnectionInProgress: true,
        checkUserStripeConnectionError: null,
      };
    case CHECK_USER_STRIPE_CONNECTION_SUCCESS:
      return {
        ...state,
        checkUserStripeConnectionInProgress: false,
        stripeConnected: payload,
      };
    case CHECK_USER_STRIPE_CONNECTION_ERROR:
      return {
        ...state,
        checkUserStripeConnectionInProgress: false,
        checkUserStripeConnectionError: payload,
      };

    case UPDATE_USER_WALLET_REQUEST:
      return {
        ...state,
        updateUserWalletInProgress: true,
        userUserWalletError: null,
      };
    case UPDATE_WALLET_SUCCESS:
      return {
        ...state,
        updateUserWalletInProgress: false,
      };
    case UPDATE_USER_WALLET_ERROR:
      return {
        ...state,
        updateUserWalletInProgress: false,
        userUserWalletError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

export const currentUserSelector = state => state.user.currentUser;

export const isCurrentUserStripeConnected = state => state.user.stripeConnected;

export const isCheckingStripeConnection = state => state.user.checkUserStripeConnectionInProgress;
// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = (transactions, unreadCount) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions, unreadCount },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const checkUserStripeConnectionRequest = () => ({
  type: CHECK_USER_STRIPE_CONNECTION_REQUEST,
});
export const checkUserStripeConnectionSuccess = isConnected => ({
  type: CHECK_USER_STRIPE_CONNECTION_SUCCESS,
  payload: isConnected,
});
export const checkUserStripeConnectionError = e => ({
  type: CHECK_USER_STRIPE_CONNECTION_ERROR,
  error: true,
  payload: e,
});

export const updateUserWalletRequest = () => ({
  type: UPDATE_USER_WALLET_REQUEST,
});
export const updateUserWalletSuccess = () => ({
  type: UPDATE_WALLET_SUCCESS,
});
export const userUserWalletError = e => ({
  type: UPDATE_USER_WALLET_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

// Safety guard so you don't accidentally loop forever
const NOTIFICATION_MAX_PAGES = 50; // 50 * 100 = 5000 tx max

export const fetchCurrentUserNotifications = () => async (dispatch, getState, sdk) => {
  const { currentUser } = getState().user;
  if (!currentUser) return null;

  const currentUserId = currentUser.id?.uuid;
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();

  if (transitionsNeedingAttention.length === 0) {
    return null;
  }

  dispatch(fetchCurrentUserNotificationsRequest());

  try {
    let page = 1;
    let allTransactions = [];
    let unreadCount = 0;

    // ✅ FIX: define Set
    const unreadTxIds = new Set();

    while (page <= NOTIFICATION_MAX_PAGES) {
      const apiQueryParams = {
        // only: userType === 'buyer' ? 'order' : 'sale',
        // last_transitions: transitionsNeedingAttention,
        page,
        perPage: NOTIFICATION_PAGE_SIZE,
        include: ['customer'],
      };

      const response = await sdk.transactions.query(apiQueryParams);
      const transactions = response.data?.data || [];

      // No more results -> stop
      if (!transactions.length) break;

      allTransactions = allTransactions.concat(transactions);
      transactions.forEach(tx => {
        const txId = tx.id?.uuid;
        const metadata = tx?.attributes?.metadata || {};
        const isCustomer = tx?.relationships?.customer?.data?.id?.uuid === currentUserId;
        let notificationData = metadata.notificationData;

        if (typeof notificationData === 'string') {
          try {
            notificationData = JSON.parse(notificationData);
          } catch {
            notificationData = [];
          }
        }

        if (!Array.isArray(notificationData)) return;

        notificationData.forEach((n, index) => {
          // ✅ Accept order + message notifications
          if (!['order', 'message', 'offer'].includes(n.type)) return;

          const notifyTo = n.notifyTo;
          const isForCurrentUser =
            (notifyTo === 'customer' && isCustomer) ||
            (notifyTo === 'provider' && !isCustomer);

          if (!isForCurrentUser) return;

          const seenBy = n.seenBy || {};
          const hasSeen = !!seenBy[currentUserId];

          if (!hasSeen) {
            unreadCount += 1;
            unreadTxIds.add(txId);
          }
        });
      });

      if (transactions.length < NOTIFICATION_PAGE_SIZE) break;
      page += 1;
    }

    const unreadTxIdList = Array.from(unreadTxIds);

    dispatch(fetchCurrentUserNotificationsSuccess(allTransactions, unreadCount));
    return { transactions: allTransactions, unreadCount, unreadTxIdList };
  } catch (e) {
    dispatch(fetchCurrentUserNotificationsError(storableError(e)));
    throw e;
  }
};


/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve({});
  }
  // Set in-progress, no errors
  dispatch(currentUserShowRequest());

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const checkUserStripeConnection = userId => async (dispatch, getState, sdk) => {
  dispatch(checkUserStripeConnectionRequest());
  try {
    const response = await SharetribeApi.checkUserStripeConnection({
      userId,
    });
    if (response.statusCode === StatusCode.SUCCESS) {
      const { isStripeConnected } = response.data;
      dispatch(checkUserStripeConnectionSuccess(isStripeConnected));
    }
    return response;
  } catch (error) {
    dispatch(checkUserStripeConnectionError(storableError(error)));
  }
};

export const updateUserWallet = params => async (dispatch, getState, sdk) => {
  dispatch(updateUserWalletRequest());
  try {
    const response = await SharetribeApi.updateUserWallet(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(updateUserWalletSuccess(response));
      return response;
    }
  } catch (error) {
    dispatch(userUserWalletError(storableError(error)));
  }
};
